import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import {connect} from 'react-redux';
import {Field, reduxForm, getFormValues} from 'redux-form';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import ListAltIcon from '@material-ui/icons/ListAlt';
import SearchIcon from '@material-ui/icons/Search';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import {FlexGridColumnGroup, FlexGridCellTemplate} from '@grapecity/wijmo.react.grid';
import {execApexRestApi, changeDateFormat, getToDate, getAddDate, getComboName,
  getDataFromGeneralPurposeMap, API_CODE, getResponseResult} from '../../../../../../common/common.js';
import MainContainer from '../../../../../organisms/MainContainer.js';
import PaperPart from '../../../../../atoms/PaperPart.js';
import BackButton from '../../../../../atoms/Buttons/BackButton.js';
import PositiveButton from '../../../../../atoms/Buttons/PositiveButton.js';
import NegativeButton from '../../../../../atoms/Buttons/NegativeButton.js';
import CustomFlexGrid from '../../../../../molecules/CustomFlexGrid.js';
import KyogaCompanyInformation from '../../../../../molecules/KyogaCompanyInformation.js';
import {renderTextField, renderSelect} from '../../../../../atoms/CustomPart.js';
import {createSearchList, createSearchListEx, changePrefectures} from '../../../../../../common/ComboParts.js';
import {commonOperations} from '../../../../../../reducks/common';
import {attachorderOperations} from '../../../../../../reducks/attachorder';
import * as validateRule from '../../../../../validateRule.js';

const styles = (theme) => ({
  fields: {
    margin: '0px 10px 15px 10px',
  },
  wave: {
    fontSize: '1.2em',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%,-50%)',
  },
  textFieldDate: {
    margin: '0px 10px 15px 10px',
    width: '42%',
    minWidth: '240px',
  },
  button: {
    margin: '5px 10px 5px 10px',
  },
});

// 申込テーブル検索時のソート条件
const appSortParams = {
  GaisanResultRegisterDate__c: -1,
};

// 申込テーブル検索時の検索対象のフィールド名
const appFields = ['OrderNo__c', 'KyogaType__c',
  'toLabel(KyogaType__c)KyogaTypeName',
  'toLabel(StbType__c)', 'RepairIraiDate__c',
  'KoziTitle__c', 'KoziPlaceMainDntNo__c', 'NWZgsyo__r.Name',
  'toLabel(KoziPlacePrefectures__c)', 'KoziPlaceMunicipalities__c',
  'KoziPlacePlace__c',
  'KoziPlaceMainDntNo_SenroName__c', // 工事場所_線路名
  'Dsp_KoziPlaceMainDntNo__c', // 工事場所_代表電柱番号
  'GaisanAmount__c', 'Fee__c', 'GaisanResultRegisterDate__c',
  'GaisanConsiderResultDeadline__c', 'toLabel(AnswerUmu__c)AnswerUmuName',
  'DrSideKoziRequiredPeriod__c',
  'OrderStatus__c', 'toLabel(OrderStatus__c)OrderStatusName',
  // 概算費用ステータスはあとから置き換え。
  'GaisanAmountStatus__c', 'AnswerUmu__c', 'RepairGaisanConsentStatus__c',
  // 画面表示以外
  'Id',
  'LastModifiedDate',
];

// 概算見積金額算定結果で使用可能な申込ステータス
const gaisanOrderStatus = [
  '15', // 改造工事費用確定
  '16', // 改造工事費用受理
  '20', // 電力工事待ち
  '21', // 電力工事完了
  '31', // 共架料算定中
  '40', // 竣工待ち／工事開始可
  '41', // 竣工確認中
  '90', // 完結
  '92', // 完結（取止め）
];

/**
 * 概算費用算定結果一覧
 * 画面ID:1481
 */
class Container extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      retButtonDisabled: true,
      buttonDisabled: true,
    };
    this.isComponentDidMount = false;
    this.gridRef = React.createRef();
    this.filterChangingHandler = this.filterChangingHandler.bind(this);
  }

  /**
   * 初期処理
   */
  async componentDidMount() {
    try {
      this.isComponentDidMount = true;
      if (this.gridRef && this.gridRef.current) {
        this.gridRef.current.setInitItems('1481', []);
      }
    } catch (error) {
      console.dir(error);
      this.sysErr();
    }
  }

  /**
   * 終了処理
   */
  async componentWillUnmount() {
    this.props.doClearOrderList();
    this.doClearList();
  }

  /**
   * 最大件数チェック
   * @return {Boolean} true:ok, false:ng
   */
  listMaxCountCheck = () => {
    const {generalPurposeMap} = this.props;
    let listCount = this.props.hasOwnProperty('orderList') &&
      this.props.orderList ? this.props.orderList.length : 0;
    // 最大件数以上の場合、エラーダイアログ表示
    const maxCount = getDataFromGeneralPurposeMap(
        generalPurposeMap, 'DisplayMaxKensu1', 1).ValueNum1__c;
    console.log({maxCount});
    if (maxCount && maxCount > 0 && listCount >= maxCount) {
      this.props.doShowMessage({
        message: 'CE0015',
      });
      return false;
    }
    return true;
  }

  /**
   * 検索ボタン押下時処理
   */
  searchOrder = async () => {
    try {
      // 画面入力されたデータのみ検索対象として取得
      const wkData = JSON.parse(JSON.stringify(this.props.values));

      if (!wkData.hasOwnProperty('GaisanAmountStatus__c')) {
        // 仕様上、概算費用ステータスが空になることはないが、念のため判定
        console.error('改造工事費用ステータスが未設定のため、選択して下さい');
        this.props.doShowMessage({
          message: {
            id: 'CE0027',
            values: ['改造工事費用ステータス'],
          },
        });
        return;
      }

      // 概算費用ステータスから申込ステータス取得
      const inGaisanSts = wkData.GaisanAmountStatus__c;
      // 申込ステータスは概算金額確定以降を使用する
      wkData['OrderStatus__c'] = gaisanOrderStatus;
      // 検索条件に概算費用ステータスの条件が入るため、削除する(申込テーブルに項目なし)
      delete wkData.GaisanAmountStatus__c;

      // 概算費用ステータスで、確定済を選択した場合、回答有無に有を設定
      if (inGaisanSts == 15) {
        wkData['AnswerUmu__c'] = '1';
      }

      // 事業所
      if ('NWZgsyo__r_Code__c' in wkData) {
        wkData['NWZgsyo__r.Code__c'] = wkData.NWZgsyo__r_Code__c;
        delete wkData.NWZgsyo__r_Code__c;
      }
      // 申込区分(改修依頼)
      wkData['OrderCategory__c'] = '05';
      // レコードタイプ
      wkData['RecordType.DeveloperName'] = 'RepairIrai';

      // 検索条件からnull、空文字条件除去
      const conditions = {};
      for (const item in wkData) {
        if (!wkData.hasOwnProperty(item)) {
          continue;
        }
        if (typeof wkData[item] == 'string' && !wkData[item].trim()) {
          continue;
        }
        if (wkData[item]) {
          conditions[item] = wkData[item];
        }
      }

      // 設計書作成依頼日
      // 設計依頼を通っている申込を取得する。日付が入っていれば設計依頼を通っている。
      conditions['DesignDocumentCreateIraiDate__c_NE'] = null;

      // 「電力側工事有無」がnullではない申込のみ取得
      conditions['DrSideKoziUmu__c_NE'] = null;

      // 申込テーブルからデータ取得
      const result = await this.props.doGetOrderList(
          conditions, appFields, appSortParams);
      const errCode = result ? result.data ? result.data.body ?
        result.data.body.errorCode : undefined : undefined : undefined;
      if (errCode != '00000') {
        this.sysErr();
        return;
      }

      // 検索結果が0件の場合、エラーダイアログ表示
      if (!this.props.hasOwnProperty('orderList') ||
        this.props.orderList.length <= 0 ) {
        this.props.doShowMessage({
          message: 'CE0016',
        });
      }

      // 最大件数チェック
      this.listMaxCountCheck();

      // 検索結果を画面表示用に更新
      const dataList = [];
      for (const item of this.props.orderList) {
        let wkOrder = item;

        // 算定前の場合（申込ステータスが14以下）、概算金額を空設定
        if (wkOrder['OrderStatus__c'] <= 14) {
          wkOrder['GaisanAmount__c'] = '';
        } else {
          if (wkOrder.hasOwnProperty('Fee__c') && wkOrder.Fee__c) {
            wkOrder['GaisanAmount__c'] += wkOrder.Fee__c;
          }
        }

        // 回答有無がnullの場合、無に置き換え
        if (!wkOrder['AnswerUmuName']) {
          wkOrder['AnswerUmuName'] = getDataFromGeneralPurposeMap(
              this.props.generalPurposeMap, 'UmuCategory0', 1).Name;
        }

        // 申込ステータスに応じた概算費用ステータス設定
        // 15以上でかつ回答有無が0は、算定済、15以上でかつ回答有無が1は、確定済
        if (wkOrder['AnswerUmu__c'] == 0 || !wkOrder['AnswerUmu__c']) {
          Object.assign(wkOrder, {'GaisanAmountStatus__c': getComboName(
              this.props.generalPurposeMap, 'GaisanCostStatus', 14)});
        } else if (wkOrder['AnswerUmu__c'] == 1) {
          Object.assign(wkOrder, {'GaisanAmountStatus__c': getComboName(
              this.props.generalPurposeMap, 'GaisanCostStatus', 15)});
        }
        dataList.push(wkOrder);
      }

      // FlexGridに初期値設定
      if (this.gridRef && this.gridRef.current) {
        if (this.isComponentDidMount == false) {
          this.gridRef.current.saveSelectedItems('1481', 'Id', true);
        }
        this.gridRef.current.setInitItems('1481', dataList);
      }
      this.isComponentDidMount = false;
    } catch (error) {
      console.dir(error);
      this.sysErr();
    }
  }

  /**
   * ボタンの活性、非活性化
   */
  checkStateUpdate = () => {
    try {
      // 一覧表示部に１件以上表示されているか判定
      let disableButton = true;
      if (this.gridRef && this.gridRef.current &&
        this.gridRef.current.selectedItems.length >= 1) {
        disableButton = false;
      }
      this.setState({retButtonDisabled: disableButton});

      let bRet = true;
      if (!disableButton) {
        bRet = false;
        for (const item of this.gridRef.current.selectedItems) {
          // 算定済以外を選択していた場合、非活性
          if (Number(item.dataItem.OrderStatus__c) != 15 ||
          (item.dataItem.AnswerUmu__c != 0 && item.dataItem.AnswerUmu__c)) {
            bRet = true;
            break;
          }
        }
      }
      this.setState({buttonDisabled: bRet});
    } catch (error) {
      console.dir(error);
      this.sysErr();
    }
  }

  /**
   * 概算費用算定結果確認ボタン押下時
   */
  doEdit = async () => {
    try {
      // チェックボックスのチェック数が1ではない場合エラー
      const selectedItems = this.gridRef.current.selectedItems;
      if (selectedItems.length == 0) {
        this.props.doShowMessage({
          message: {
            id: 'CE0027',
            values: ['編集対象'],
          },
        });
        return;
      }

      if (selectedItems.length != 1) {
        this.props.doShowMessage({
          message: 'CE0022',
        });
        return;
      }

      this.props.doSetOrderId(selectedItems[0].dataItem.Id);
      this.props.history.push({pathname: '/RepairIrai/KoziDone/GaisanCostCalculationResultConfirm/Confirm'});
    } catch (error) {
      console.dir(error);
      this.sysErr();
    }
  }

  /**
   * 了承ボタン押下時
   */
  doAccept= async () => {
    try {
      // チェックボックスのチェック数が1ではない場合エラー
      const selectedItems = this.gridRef.current.selectedItems;
      if (selectedItems.length == 0) {
        this.props.doShowMessage({
          message: {
            id: 'CE0027',
            values: ['編集対象'],
          },
        });
        return;
      }

      const wkSelectData = [];
      for (const item of selectedItems) {
        // 申込ステータスが「概算金額確定」のものを抽出
        if (Number(item.dataItem.OrderStatus__c) == 15) {
          wkSelectData.push(item.dataItem);
        }
      }
      if (wkSelectData.length <= 0) {
        this.props.doShowMessage({
          message: {
            id: 'CE0027',
            values: ['改造工事費用が確定した申込'],
          },
        });
        return;
      }

      this.props.doShowMessage({
        message: {
          id: 'CC0005',
          values: ['了承'],
        },
        action: () => {
          this.doOrderAcceptExec(wkSelectData);
        },
      });
    } catch (error) {
      console.dir(error);
      this.sysErr();
    }
  }

  /**
   * 了承ボタン押下時処理
   * @param {Object} selectedItem 更新対象
   * @return {void}
   */
  doOrderAcceptExec= async (selectedItem) => {
    const {userInfo} = this.props;
    try {
      // 検索条件作成
      const orderList = [];
      for (const value in selectedItem) {
        // 自身のプロパティであるか判定(エラー回避)
        if (selectedItem.hasOwnProperty(value)) {
          const order = {
            'conditions': {
              'Id': selectedItem[value].Id,
              'LastModifiedDate': selectedItem[value].LastModifiedDate,
            },
            'order': {
              'Id': selectedItem[value].Id,
              'OrderStatus__c': '16',  // 申込ステータス(概算金額受理)
              'AnswerUmu__c': '1',     // 回答有無(有)
              'GaisanAmountConsentDate__c': getToDate(), // 概算金額承諾日(システム日付)
              'RepairGaisanConsentStatus__c': '1', // 承諾
            },
          };
          orderList.push(order);
        }
      }

      const body = {
        'record': orderList,
        'stage': 'REPAIR_ORDER_ACCEPT_UPDATE',
      };

      const result = await execApexRestApi(
          userInfo, 'ApiAttachOrder/gaisanCostCalculationAccept', body);
      const resResult = getResponseResult(result, ['選択項目', '更新']);
      if (resResult.errorCode != API_CODE.SUCCESS) {
        this.props.doShowMessage({
          message: resResult.messages,
        });
        return;
      }

      this.gridRef.current.saveScreenState('1481', 'Id');
      this.isComponentDidMount = true;
      // 再検索
      this.searchOrder();
    } catch (error) {
      console.dir(error);
      this.sysErr();
    }
  }

  /**
   * 改造工事取下ボタン押下時
   */
  doGaisenCanceled= async () => {
    try {
      // チェックボックスのチェック数が1ではない場合エラー
      const selectedItems = this.gridRef.current.selectedItems;
      if (selectedItems.length == 0) {
        this.props.doShowMessage({
          message: {
            id: 'CE0027',
            values: ['編集対象'],
          },
        });
        return;
      }

      const wkSelectData = [];
      for (const item of selectedItems) {
        // 申込ステータスが「概算金額確定」のものを抽出
        if (Number(item.dataItem.OrderStatus__c) == 15) {
          wkSelectData.push(item.dataItem);
        }
      }
      if (wkSelectData.length <= 0) {
        this.props.doShowMessage({
          message: {
            id: 'CE0027',
            values: ['改造工事費用が確定した申込'],
          },
        });
        return;
      }

      this.props.doShowMessage({
        message: {
          id: 'CC0005',
          values: ['改造工事取下'],
        },
        action: () => {
          this.doGaisenCanceledExec(wkSelectData);
        },
      });
    } catch (error) {
      console.dir(error);
      this.sysErr();
    }
  }

  /**
   * 改造工事取下ボタン押下時処理
   * @param {Object} selectedItem 更新対象
   * @return {void}
   */
  doGaisenCanceledExec= async (selectedItem) => {
    const {userInfo} = this.props;
    try {
      // 検索条件作成
      const orderList = [];
      for (const value in selectedItem) {
        // 自身のプロパティであるか判定(エラー回避)
        if (selectedItem.hasOwnProperty(value)) {
          const order = {
            'conditions': {
              'Id': selectedItem[value].Id,
              'LastModifiedDate': selectedItem[value].LastModifiedDate,
            },
            'order': {
              'Id': selectedItem[value].Id,
              'OrderStatus__c': '16',  // 申込ステータス(概算金額受理)
              'AnswerUmu__c': '1',     // 回答有無(有)
              'RepairGaisanConsentStatus__c': '2', // 取下げ
            },
          };
          orderList.push(order);
        }
      }

      const body = {
        'record': orderList,
        'stage': 'REPAIR_ORDER_GAISEN_CANCELED_UPDATE',
      };

      const result = await execApexRestApi(
          userInfo, 'ApiAttachOrder/gaisanCostCalculationAccept', body);
      const errCode = result ? result.data ? result.data.body ?
        result.data.body.errorCode : undefined : undefined : undefined;
      if (errCode != '00000') {
        this.props.doShowMessage({
          message: {
            id: 'CE0052',
            values: ['選択項目', '更新'],
          },
        });
        return;
      }
      this.gridRef.current.saveScreenState('1481', 'Id');
      this.isComponentDidMount = true;
      // 再検索
      this.searchOrder();
    } catch (error) {
      console.dir(error);
      this.sysErr();
    }
  }

  /**
   * 一覧をクリアする
   */
  doClearList = () => {
    if (this.gridRef && this.gridRef.current) {
      this.gridRef.current.saveScreenState('1481', 'Id');
      this.gridRef.current.setItems(null);
    }
  }

  /**
   * システムエラー
   */
  sysErr = () => {
    this.props.doShowMessage({
      message: 'CS0001',
      action: () => {
        this.props.history.push('/login');
      },
    });
  }

  /**
   * 一覧のフィルターを編集する
   * @param {object} s
   * @param {object} e イベント
   */
  filterChangingHandler(s, e) {
    if (e.getColumn().binding === 'RepairGaisanConsentStatus__c') {
      let edt = s.activeEditor;
      let lbHost = edt.hostElement.querySelector('[wj-part=div-values]');
      let lb = wijmo.Control.getControl(lbHost);

      let categoryName = 'RepairGaisanConsentStatus'; // 見積結果承諾状況

      lb.itemFormatter = (index) => {
        const ret = getComboName(this.props.generalPurposeMap,
            categoryName, lb.collectionView.items[index].value);
        return ret ? ret : '(なし)';
      };
      lb.collectionView.refresh();
    }
  }

  renderTable() {
    const props = {
      rowHeaderType: 'check',
      filterOn: true,
      headersVisibility: 'All',
      allowDragging: 'None',
      counterOn: false,
      style: {maxHeight: '400px'},
      isReadOnly: true,
      checkedFunction: this.checkStateUpdate,
      filterChanging: this.filterChangingHandler,
    };

    return (
      // 一覧表示部の表示設定
      <div className="container-fluid">
        <CustomFlexGrid ref={this.gridRef} {...props}>
          <FlexGridColumnGroup binding='OrderNo__c' header='改修依頼番号' dataType="String"></FlexGridColumnGroup>
          <FlexGridColumnGroup binding='KyogaTypeName' header='共架種別' dataType="String"></FlexGridColumnGroup>
          <FlexGridColumnGroup binding='StbType__c' header='設備種別' dataType="String" width={100}></FlexGridColumnGroup>
          <FlexGridColumnGroup binding='RepairIraiDate__c' header='改修依頼日' dataType="Date" width={110}>
            <FlexGridCellTemplate cellType="Cell"
              template= {(context) => {
                return changeDateFormat(context.item.RepairIraiDate__c);
              }}
            />
          </FlexGridColumnGroup>
          <FlexGridColumnGroup binding='KoziTitle__c' header='工事件名' dataType="String"></FlexGridColumnGroup>
          <FlexGridColumnGroup binding='NWZgsyo__r.Name' header='事業所名' dataType="String"></FlexGridColumnGroup>
          <FlexGridColumnGroup header='工事場所'>
            <FlexGridColumnGroup binding="KoziPlaceMainDntNo_SenroName__c" header="線路名" dataType="String"></FlexGridColumnGroup>
            <FlexGridColumnGroup binding='Dsp_KoziPlaceMainDntNo__c' header='代表電柱番号' dataType="String"></FlexGridColumnGroup>
            <FlexGridColumnGroup binding='KoziPlacePrefectures__c' header='都道府県' dataType="String" width={100}></FlexGridColumnGroup>
            <FlexGridColumnGroup binding='KoziPlaceMunicipalities__c' header='市区町村' dataType="String"></FlexGridColumnGroup>
            <FlexGridColumnGroup binding='KoziPlacePlace__c' header='場所' dataType="String"></FlexGridColumnGroup>
          </FlexGridColumnGroup>
          <FlexGridColumnGroup binding='GaisanAmount__c' header='改造工事費用\n（税抜）' dataType="Number" width={130}></FlexGridColumnGroup>
          <FlexGridColumnGroup binding='GaisanResultRegisterDate__c' header='改造工事費用結果\n登録日' dataType="Date" width={160}>
            <FlexGridCellTemplate cellType="Cell"
              template= {(context) => {
                return changeDateFormat(
                    context.item.GaisanResultRegisterDate__c);
              }}
            />
          </FlexGridColumnGroup>
          <FlexGridColumnGroup binding='GaisanConsiderResultDeadline__c' header='回答期限' dataType="Date" width={110}>
            <FlexGridCellTemplate cellType="Cell"
              template= {(context) => {
                return changeDateFormat(
                    context.item.GaisanConsiderResultDeadline__c);
              }}
            />
          </FlexGridColumnGroup>
          <FlexGridColumnGroup binding='AnswerUmuName' header='回答有無' cssClass="centerPos" dataType="String" width={100}></FlexGridColumnGroup>
          <FlexGridColumnGroup binding='RepairGaisanConsentStatus__c' header='見積結果\n承諾状況' width={120} dataType="String"isReadOnly={true}>
            <FlexGridCellTemplate cellType="Cell"
              template={(context) =>{
                return getComboName(this.props.generalPurposeMap, 'RepairGaisanConsentStatus', context.item.RepairGaisanConsentStatus__c);
              }

              }
            />
          </FlexGridColumnGroup>
          <FlexGridColumnGroup binding='DrSideKoziRequiredPeriod__c' header='改造工事期間(目安)\n〔か月〕' dataType="Number" format="n1" width={165}></FlexGridColumnGroup>
          <FlexGridColumnGroup binding='OrderStatusName' header='申込ステータス' dataType="String"></FlexGridColumnGroup>
          <FlexGridColumnGroup binding='GaisanAmountStatus__c' header='改造工事費用\nステータス' dataType="String" width={135}></FlexGridColumnGroup>
        </CustomFlexGrid>
      </div>
    );
  }

  render() {
    const {classes, generalPurposeMap, reset,
      handleSubmit, userInfo} = this.props;

    const footerBtn = (
      <Grid container
        justifyContent="center"
        alignItems="flex-start"
        spacing={1}
      >
        <Grid key="btn1" item>
          <PositiveButton
            startIcon={<ListAltIcon />}
            onClick={handleSubmit(this.doEdit)}
            variant="contained"
            size="large"
            type="button"
            disabled={this.state.retButtonDisabled}
          >
            <span>改造工事費用算定結果確認</span>
          </PositiveButton>
        </Grid>

        <Grid key="btn2" item>
          <PositiveButton
            onClick={handleSubmit(this.doAccept)}
            variant="contained"
            size="large"
            type="button"
            disabled={this.state.buttonDisabled}
          >
            <span>了承</span>
          </PositiveButton>
        </Grid>

        <Grid key="btn3" item>
          <PositiveButton
            onClick={handleSubmit(this.doGaisenCanceled)}
            variant="contained"
            size="large"
            type="button"
            disabled={this.state.buttonDisabled}
          >
            <span>改造工事取下</span>
          </PositiveButton>
        </Grid>

        <Grid key="btn4" item>
          <BackButton props={this.props}/>
        </Grid>
      </Grid>
    );

    return (
      <form noValidate autoComplete="off">
        <MainContainer
          props={this.props}
          footerBtn={footerBtn}
        >

          <KyogaCompanyInformation companyName={userInfo.Account.Name} />

          <PaperPart>
            <Grid
              container
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={2}
            >

              <Grid key="key1-1" item xs={12} sm={3}>
                <Field
                  name="OrderNo__c"
                  className={classes.fields}
                  component={renderTextField}
                  label="改修依頼番号"
                  fullWidth
                  type="text"
                  required={false}
                  validate={[
                    validateRule.alphaNumericSymbol,
                    validateRule.maxLength(12),
                  ]}
                />
              </Grid>

              <Grid key="key1-2" item xs={12} sm={3}>
                <Field
                  name="NWZgsyo__r_Code__c"
                  className={classes.fields}
                  component={renderSelect}
                  label="事業所"
                  fullWidth
                  required={false}
                  validate={[
                  ]}
                >
                  {generalPurposeMap &&
                     createSearchListEx(generalPurposeMap.NWZgsyo, 'Code__c', 'Name', null, {Code__c: ['1001', '1065']})}
                </Field>
              </Grid>

              <Grid key="key1-3" item xs={12} sm={3}>
                <Field
                  name="StbType__c"
                  className={classes.fields}
                  component={renderSelect}
                  label="設備種別"
                  fullWidth
                  required={false}
                  validate={[
                  ]}
                >
                  {generalPurposeMap &&
                     createSearchList(generalPurposeMap.StbType,
                         'Code__c', 'Name', {Code__c: ['1', '2']})}
                </Field>
              </Grid>

              <Grid key="key1-4" item xs={12} sm={3}>
                <Field
                  name="KyogaType__c"
                  className={classes.fields}
                  component={renderSelect}
                  label="共架種別"
                  fullWidth
                  required={false}
                  validate={[
                  ]}
                >
                  {generalPurposeMap &&
                    createSearchList(generalPurposeMap.KyogaType,
                        'Code__c', 'Name',
                        {'Code__c': userInfo.Account.Account__r.KyogaType__c,
                          'ValueString1__c': Object.hasOwnProperty.call(
                              this.props.values, 'StbType__c') ?
                              [this.props.values.StbType__c] : ['1', '2']})}
                </Field>
              </Grid>

              <Grid key="key1-5" item xs={12} sm={3}>
                <Field
                  name="KoziTitle__c"
                  className={classes.fields}
                  component={renderTextField}
                  label="工事件名"
                  fullWidth
                  type="text"
                  required={false}
                  validate={[
                    validateRule.maxLength(50),
                  ]}
                />
              </Grid>

              <Grid key="key1-6" item xs={12} sm={3}>
                <Field
                  select
                  name="GaisanAmountStatus__c"
                  className={classes.fields}
                  component={renderSelect}
                  label="改造工事費用ステータス"
                  fullWidth
                  required={false}
                  validate={[
                  ]}
                >
                  {generalPurposeMap &&
                     createSearchList(generalPurposeMap.GaisanCostStatus,
                         'Code__c', 'Name', {Code__c: ['14', '15']}, 'false')}
                </Field>
              </Grid>

              <Grid key="key1-6-2" item xs={12} sm={3}>
                <Field
                  name="RepairGaisanConsentStatus__c"
                  component={renderSelect}
                  className={classes.fields}
                  label="見積結果承諾状況"
                  fullWidth
                  validate={[
                  ]}
                >
                  {generalPurposeMap &&
                      createSearchList(
                          generalPurposeMap.RepairGaisanConsentStatus)}
                </Field>
              </Grid>

            </Grid>

            <Grid
              container
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={2}
            >

              <Grid key="key1-7" item xs={12} sm={2}>
                <Field
                  name="KoziPlaceMainDntNo_SenroName__c"
                  className={classes.fields}
                  component={renderTextField}
                  label="線路名"
                  fullWidth
                  type="text"
                  required={false}
                  validate={[
                    validateRule.maxLength(30),
                  ]}
                />
              </Grid>

              <Grid key="key1-7-2" item xs={12} sm={2}>
                <Field
                  name="Dsp_KoziPlaceMainDntNo__c"
                  className={classes.fields}
                  component={renderTextField}
                  label="代表電柱番号"
                  fullWidth
                  type="text"
                  required={false}
                  validate={[
                    validateRule.maxLength(23),
                  ]}
                />
              </Grid>
              <Grid key="key2-1" item xs={12} sm={2}>
                <Field
                  name="KoziPlacePrefectures__c"
                  className={classes.fields}
                  component={renderSelect}
                  label="都道府県"
                  fullWidth
                  required={false}
                  validate={[
                  ]}
                  onChange={(event) => {
                    changePrefectures(event, this);
                  }}
                >
                  {generalPurposeMap &&
                    createSearchList(generalPurposeMap.PrefCode)}
                </Field>
              </Grid>

              <Grid key="key2-2" item xs={12} sm={3}>
                <Field
                  name="KoziPlaceMunicipalities__c"
                  className={classes.fields}
                  component={renderTextField}
                  label="市区町村"
                  fullWidth
                  type="text"
                  required={false}
                  validate={[
                    validateRule.maxLength(12),
                  ]}
                />
              </Grid>

              <Grid key="key2-3" item xs={12} sm={3}>
                <Field
                  name="KoziPlacePlace__c"
                  className={classes.fields}
                  component={renderTextField}
                  label="場所"
                  fullWidth
                  type="text"
                  required={false}
                  validate={[
                    validateRule.maxLength(255),
                  ]}
                />
              </Grid>

            </Grid>

            <Grid
              container
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={1}
            >
              <Grid key="key3-1" item xs={12} md={6} style={{position: 'relative'}}>
                <div style={{position: 'relative'}}>
                  <Field
                    name="RepairIraiDate__c_From"
                    accessibilitylabel="RepairIraiDate__c_From"
                    label="改修依頼年月日"
                    component={renderTextField}
                    className={classes.textFieldDate}
                    required={false}
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    validate={[
                      validateRule.date,
                    ]}
                  />

                  <span className={classes.wave}>～</span>

                  <Field
                    name="RepairIraiDate__c_To"
                    accessibilitylabel="RepairIraiDate__c_To"
                    label={<React.Fragment />}
                    component={renderTextField}
                    className={classes.textFieldDate}
                    required={false}
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    validate={[
                      validateRule.date,
                    ]}
                  />
                </div>
              </Grid>

              <Grid key="key3-2" item xs={12} md={6}>
                <div style={{position: 'relative'}}>
                  <Field
                    name="GaisanResultRegisterDate__c_From"
                    accessibilitylabel="GaisanResultRegisterDate__c_From"
                    label="改造工事費用結果登録日"
                    component={renderTextField}
                    className={classes.textFieldDate}
                    required={false}
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    validate={[
                      validateRule.date,
                    ]}
                  />

                  <span className={classes.wave}>～</span>

                  <Field
                    name="GaisanResultRegisterDate__c_To"
                    accessibilitylabel="GaisanResultRegisterDate__c_To"
                    label={<React.Fragment />}
                    component={renderTextField}
                    className={classes.textFieldDate}
                    required={false}
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    validate={[
                      validateRule.date,
                    ]}
                  />
                </div>
              </Grid>

              <Grid key="key4-3" item xs={6} md={6}>
                <NegativeButton
                  variant="contained"
                  startIcon={<RotateLeftIcon />}
                  className={classes.button}
                  size="large"
                  onClick={reset}
                >
                  <span>クリア</span>
                </NegativeButton>

                <PositiveButton
                  variant="contained"
                  startIcon={<SearchIcon />}
                  className={clsx(classes.button)}
                  size="large"
                  onClick={handleSubmit(this.searchOrder)}
                >
                  <span>検索</span>
                </PositiveButton>
              </Grid>
            </Grid>
          </PaperPart>

          <div style={{marginBottom: '20px'}} />

          <Divider variant="middle" />

          <div style={{marginBottom: '20px'}} />
          <Grid
            container
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Grid key="key-table" item xs={12}>
              {this.renderTable()}
            </Grid>
          </Grid>
        </MainContainer>
      </form>
    );
  }
}

Container.propTypes = {
  classes: PropTypes.object.isRequired,
  generalPurposeMap: PropTypes.object,
  orderList: PropTypes.array,
  history: PropTypes.object.isRequired,
  values: PropTypes.object,
  userInfo: PropTypes.object,
  reset: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  doShowMessage: PropTypes.func.isRequired,
  doGetOrderList: PropTypes.func.isRequired,
  doSetOrderId: PropTypes.func.isRequired,
  doClearOrderList: PropTypes.func,
};

const mapStateToProps = (state) => ({
  currentTheme: state.style.theme,
  generalPurposeMap: state.common.generalPurposeMap,
  userInfo: state.auth.userInfo,
  orderList: state.attachorder.orderList,
  // 表示時のデフォルト設定
  initialValues: {
    GaisanAmountStatus__c: 14, // 算定済のコード値
    RepairIraiDate__c_From: getAddDate(),
  },
});

const mapDispatchToProps = {
  doShowMessage: commonOperations.doShowMessage,
  doGetOrderList: attachorderOperations.doGetOrderList,
  doSetOrderId: attachorderOperations.doSetOrderId,
  doClearOrderList: attachorderOperations.doClearOrderList,
};

const FORM_NAME = 'RepairIraiGaisanCostCalculationResultList';

Container = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: true,
})(connect((state) => {
  return {
    values: getFormValues(FORM_NAME)(state),
  };
})(Container));

export default withStyles(styles)(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(Container),
);
